import { useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface LearningRecordParams {
  videoId: string;
  duration: number;
  currentTime: number;
}

export function useLearningRecords({ videoId, duration, currentTime }: LearningRecordParams) {
  const { user } = useAuth();
  const lastSaveTimeRef = useRef<number>(0);
  const watchStartTimeRef = useRef<number>(Date.now());
  const totalWatchTimeRef = useRef<number>(0);

  const currentTimeRef = useRef(currentTime);

  useEffect(() => {
    currentTimeRef.current = currentTime;
  }, [currentTime]);

  // Save progress to database
  const saveProgress = useCallback(async () => {
    if (!user || !videoId || duration <= 0) return;

    const current = currentTimeRef.current;
    const now = Date.now();
    const sessionWatchTime = Math.floor((now - watchStartTimeRef.current) / 1000);
    totalWatchTimeRef.current += sessionWatchTime;
    watchStartTimeRef.current = now;

    const progress = Math.min((current / duration) * 100, 100);
    const completed = progress >= 90;

    try {
      // Check if record exists
      const { data: existing } = await supabase
        .from("learning_records")
        .select("id, watch_duration")
        .eq("user_id", user.id)
        .eq("video_id", videoId)
        .maybeSingle();

      if (existing) {
        // Update existing record
        const { error: updateError } = await supabase
          .from("learning_records")
          .update({
            last_position: current,
            progress,
            watch_duration: (existing.watch_duration || 0) + sessionWatchTime,
            completed,
            updated_at: new Date().toISOString(),
          })
          .eq("id", existing.id);
        if (updateError) throw updateError;
      } else {
        // Insert new record
        const { error: insertError } = await supabase
          .from("learning_records")
          .insert({
            user_id: user.id,
            video_id: videoId,
            last_position: current,
            progress,
            watch_duration: sessionWatchTime,
            completed,
          });
        if (insertError) throw insertError;
      }

      lastSaveTimeRef.current = now;
    } catch (error) {
      console.error("Error saving learning record:", error);
    }
  }, [user, videoId, duration]); // Removed currentTime dependency

  // Auto-save every 15 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (currentTimeRef.current > 0) {
        saveProgress();
      }
    }, 15000);

    return () => clearInterval(interval);
  }, [saveProgress]);

  // Save on unmount
  useEffect(() => {
    return () => {
      if (currentTimeRef.current > 0) {
        saveProgress();
      }
    };
  }, [saveProgress]);

  // Save when video ends or user leaves
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden" && currentTimeRef.current > 0) {
        void saveProgress();
      }
    };

    const onPageHide = () => {
      if (currentTimeRef.current > 0) {
        void saveProgress();
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [saveProgress]);

  // Load last position on mount
  const loadLastPosition = useCallback(async (): Promise<number | null> => {
    if (!user || !videoId) return null;

    try {
      const { data } = await supabase
        .from("learning_records")
        .select("last_position")
        .eq("user_id", user.id)
        .eq("video_id", videoId)
        .maybeSingle();

      return data?.last_position || null;
    } catch (error) {
      console.error("Error loading last position:", error);
      return null;
    }
  }, [user, videoId]);

  return {
    saveProgress,
    loadLastPosition,
  };
}
